import UploadFileIcon from "@mui/icons-material/UploadFileOutlined";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { VALIDATION_TYPE_LABELS } from "../../domain/request.types";
import type { WizardFormState } from "./useWizardState";

export interface StepAttachmentsReviewProps {
  form: WizardFormState;
  update: <K extends keyof WizardFormState>(key: K, value: WizardFormState[K]) => void;
  teamName: string;
}

/**
 * Files stay as local `File` objects in wizard state through review - they're
 * only actually uploaded (as category `Evidence`) once the request itself
 * has been created and has an id, in `RequestWizardPage.handleSubmit`.
 */
export function StepAttachmentsReview({ form, update, teamName }: StepAttachmentsReviewProps) {
  function handleFilesSelected(fileList: FileList | null) {
    if (!fileList) return;
    update("attachments", [...form.attachments, ...Array.from(fileList)]);
  }

  function removeAttachment(index: number) {
    update(
      "attachments",
      form.attachments.filter((_, i) => i !== index),
    );
  }

  return (
    <Stack spacing={3} sx={{ pt: 1, maxWidth: 560 }}>
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Supporting documents
        </Typography>
        <Button component="label" variant="outlined" startIcon={<UploadFileIcon />}>
          Choose files
          <input
            type="file"
            multiple
            hidden
            onChange={(event) => handleFilesSelected(event.target.files)}
          />
        </Button>
        {form.attachments.length > 0 ? (
          <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 1.5 }}>
            {form.attachments.map((file, index) => (
              <Chip
                key={`${file.name}-${index}`}
                label={file.name}
                onDelete={() => removeAttachment(index)}
              />
            ))}
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            No files attached yet.
          </Typography>
        )}
      </Box>

      <Divider />

      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Review
        </Typography>
        <Stack spacing={0.75}>
          <ReviewRow label="Team" value={teamName || "—"} />
          <ReviewRow
            label="Validation type"
            value={form.validationType ? VALIDATION_TYPE_LABELS[form.validationType] : "—"}
          />
          <ReviewRow label="Title" value={form.title || "—"} />
          <ReviewRow label="Description" value={form.description || "—"} />
          <ReviewRow label="Attachments" value={String(form.attachments.length)} />
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
          Priority and expected completion date are set later by the Validation Manager when the
          request is accepted, not here.
        </Typography>
      </Box>
    </Stack>
  );
}

function ReviewRow({ label, value }: { label: string; value: string }) {
  return (
    <Stack direction="row" spacing={1}>
      <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 140 }}>
        {label}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: "anywhere" }}>
        {value}
      </Typography>
    </Stack>
  );
}
